// inflation.ts — the core deflator for Inflationsbereinigung (Feature 2, frontend-only
// real-terms display). Presentation only: nothing here touches the API or localStorage,
// and the engine's nominal SimulationResult is never mutated — see lib/realTerms.ts for
// how these two helpers get applied across the UI (summary cards, chart series,
// waterfall, compare table, Monte-Carlo band + stats).
import { roundCentsHalfAwayFromZero } from '@etf-cockpit/engine';
import type { Bp, Cents } from '@etf-cockpit/engine';

const MS_PER_YEAR = 365.2425 * 24 * 60 * 60 * 1000; // Gregorian mean year

/**
 * Elapsed years (fractional, can be negative) between two ISO dates (YYYY-MM-DD).
 * Display-only precision — Feature 2's deflator is a presentation transform, not a
 * tax-path calculation (D-003 doesn't apply here), so a Gregorian-mean-year
 * approximation is adequate rather than exact calendar/day-count math.
 */
export function yearsBetween(fromIso: string, toIso: string): number {
  const from = Date.parse(`${fromIso}T00:00:00Z`);
  const to = Date.parse(`${toIso}T00:00:00Z`);
  return (to - from) / MS_PER_YEAR;
}

/**
 * Deflates a cents amount to "today's purchasing power": divides by
 * (1 + inflationBp/10000)^years, rounded half away from zero (same convention as every
 * other cents rounding in this codebase, via the engine's own roundCentsHalfAwayFromZero
 * — even though this is a display-only transform, not a tax-path one, there is no reason
 * to invent a second rounding rule). inflationBp === 0 or years === 0 returns the input
 * unchanged (exact identity, not just "close").
 */
export function deflate(cents: Cents, years: number, inflationBp: Bp): Cents {
  if (inflationBp === 0 || years === 0) return cents;
  const factor = Math.pow(1 + inflationBp / 10000, years);
  return roundCentsHalfAwayFromZero(cents / factor);
}
